<script setup lang="ts">
import { computed } from 'vue'

import FlexComponent from '../atoms/FlexComponent.vue'
import InputComponent from '../atoms/InputComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'
import Card from '../molecules/Card.vue'

defineSlots<{
  advertiserGroup?(props: { isInvalid: boolean; errorId: string | undefined }): void
}>()

const props = withDefaults(
  defineProps<{
    isDisabled?: boolean
    isNameInvalid?: boolean
    nameErrorMessage?: string
    isAdvertiserGroupInvalid?: boolean
    advertiserGroupErrorMessage?: string
  }>(),
  {
    isDisabled: false,
    isNameInvalid: false,
    nameErrorMessage: 'Enter a segment name',
    isAdvertiserGroupInvalid: false,
    advertiserGroupErrorMessage: 'Select an advertiser group',
  },
)

const name = defineModel<string>('name', { default: '' })
const description = defineModel<string>('description', { default: '' })

const showNameError = computed(() => props.isNameInvalid && !name.value.trim())
const nameErrorId = `segment-name-${crypto.randomUUID()}`
const advertiserGroupErrorId = `segment-advertiser-group-${crypto.randomUUID()}`

const TITLE = 'Basic information'
const NAME_LABEL = 'Segment name'
const DESCRIPTION_LABEL = 'Description'
const ADVERTISER_GROUP_LABEL = 'Advertiser group'
const DESCRIPTION_PLACEHOLDER = 'Describe the purpose of this segment.'
</script>

<template>
  <Card :title="TITLE">
    <FlexComponent direction="column" :gap="16">
      <FlexComponent direction="column" :gap="8">
        <InputComponent
          v-model="name"
          type="text"
          :is-disabled="isDisabled"
          :name="NAME_LABEL"
          :aria-invalid="showNameError || undefined"
          :aria-describedby="showNameError ? nameErrorId : undefined"
        >
          <template #header>
            <TypographyComponent variant="title-large" :font-weight="600">{{ NAME_LABEL }}</TypographyComponent>
          </template>
        </InputComponent>

        <TypographyComponent v-if="showNameError" :id="nameErrorId" role="alert" variant="label-small" color="var(--error)">
          {{ nameErrorMessage }}
        </TypographyComponent>
      </FlexComponent>

      <InputComponent
        v-model="description"
        type="text"
        :is-disabled="isDisabled"
        :placeholder="DESCRIPTION_PLACEHOLDER"
        :name="DESCRIPTION_LABEL"
      >
        <template #header>
          <TypographyComponent variant="title-large" :font-weight="600">
            {{ DESCRIPTION_LABEL }} <span class="segment-basic-information__optional">(optional)</span>
          </TypographyComponent>
        </template>
      </InputComponent>

      <FlexComponent v-if="$slots.advertiserGroup" direction="column" :gap="8" class="segment-basic-information__field">
        <TypographyComponent variant="title-large" :font-weight="600">{{ ADVERTISER_GROUP_LABEL }}</TypographyComponent>
        <slot
          name="advertiserGroup"
          :is-invalid="isAdvertiserGroupInvalid"
          :error-id="isAdvertiserGroupInvalid ? advertiserGroupErrorId : undefined"
        />

        <TypographyComponent
          v-if="isAdvertiserGroupInvalid"
          :id="advertiserGroupErrorId"
          role="alert"
          variant="label-small"
          color="var(--error)"
        >
          {{ advertiserGroupErrorMessage }}
        </TypographyComponent>
      </FlexComponent>
    </FlexComponent>
  </Card>
</template>

<style scoped lang="scss">
.segment-basic-information__optional {
  font-weight: 400;
}

.segment-basic-information__field {
  :deep(.el-dropdown) {
    display: flex;
    width: 100%;
  }

  :deep(.box.selector-label) {
    box-sizing: border-box;
    width: 100%;
    max-width: none;
    min-height: 56px;
    background: var(--inverted);
    border: 1px solid var(--secondary-12);
    border-radius: 8px;
  }

  :deep(.box.selector-label .flex) {
    justify-content: space-between;
  }
}
</style>
